import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { calculatePnl as calcPnl, getPnlMultiplier } from "@/utils/pnl";

const EXIT_TYPES = [
  { value: "PM1", label: "PM1 - Profit Booking 1" },
  { value: "PM2", label: "PM2 - Profit Booking 2" },
  { value: "PM3", label: "PM3 - Profit Booking 3" },
  { value: "Target", label: "Target Hit" },
  { value: "Trail", label: "Trailing Stop" },
  { value: "StopLoss", label: "Stop Loss" },
  { value: "Partial", label: "Partial Exit" },
];

export const PartialCloseModal = ({ isOpen, onClose, trade, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    exit_date: new Date().toISOString().split("T")[0],
    exit_price: "",
    exit_lots: 1,
    exit_type: "PM1",
    notes: "",
  });

  // Calculate remaining lots
  const totalLots = trade?.num_lots || 0;
  const exitedLots = trade?.total_exited_lots || 0;
  const remainingLots = trade?.remaining_lots ?? totalLots;

  useEffect(() => {
    if (isOpen && trade) {
      setForm({
        exit_date: new Date().toISOString().split("T")[0],
        exit_price: trade.target || "",
        exit_lots: Math.min(1, remainingLots),
        exit_type: "PM1",
        notes: "",
      });
    }
  }, [isOpen, trade, remainingLots]);

  // Calculate P&L preview using correct multiplier
  const calculatePnL = () => {
    if (!form.exit_price || !form.exit_lots || !trade) return { points: 0, amount: 0 };
    
    const exitPrice = parseFloat(form.exit_price);
    const { pnlPoints, pnlAmount } = calcPnl({
      direction: trade.direction,
      entryPrice: trade.entry_price,
      exitPrice,
      numLots: form.exit_lots,
      trade,
    });
    return { points: pnlPoints, amount: pnlAmount };
  };

  const pnl = calculatePnL();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.exit_price || !form.exit_lots || !form.exit_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (form.exit_lots > remainingLots) {
      toast.error(`Cannot exit more than ${remainingLots} remaining lots`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/trades/${trade.id}/partial-close`, {
        exit_date: form.exit_date,
        exit_price: parseFloat(form.exit_price),
        exit_lots: parseInt(form.exit_lots),
        exit_type: form.exit_type,
        notes: form.notes || null,
      });

      toast.success(response.data.message);
      onSuccess?.(response.data.trade);
      onClose();
    } catch (error) {
      console.error("Partial close error:", error);
      toast.error(error.response?.data?.detail || "Failed to partially close trade");
    } finally {
      setLoading(false);
    }
  };

  if (!trade) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <span className="text-cyan-400">Partial Close</span>
            <span className={trade.direction === "LONG" ? "text-profit" : "text-loss"}>
              {trade.symbol}
            </span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Trade Info Summary */}
          <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Direction</span>
              <span className={trade.direction === "LONG" ? "text-profit font-medium" : "text-loss font-medium"}>
                {trade.direction}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Entry Price</span>
              <span className="text-white font-mono">₹{trade.entry_price?.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Total Lots</span>
              <span className="text-white">{totalLots} lots × {trade.lot_size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Already Exited</span>
              <span className="text-yellow-400">{exitedLots} lots</span>
            </div>
            <div className="flex justify-between border-t border-slate-700 pt-2">
              <span className="text-slate-400 font-medium">Remaining</span>
              <span className="text-cyan-400 font-bold">{remainingLots} lots</span>
            </div>
          </div>

          {/* Exit Legs History */}
          {trade.exit_legs && trade.exit_legs.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-2">Previous Exit Legs:</p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {trade.exit_legs.map((leg, idx) => (
                  <div key={leg.id || idx} className="flex justify-between text-xs">
                    <span className="text-slate-500">
                      {leg.exit_type || "Partial"}: {leg.exit_lots} lots @ ₹{leg.exit_price?.toLocaleString('en-IN')}
                    </span>
                    <span className={leg.pnl_amount >= 0 ? "text-profit" : "text-loss"}>
                      ₹{leg.pnl_amount?.toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Exit Date *</Label>
              <Input
                type="date"
                value={form.exit_date}
                onChange={(e) => setForm({ ...form, exit_date: e.target.value })}
                className="bg-slate-800 border-slate-600 text-white"
                required
                data-testid="partial-close-exit-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Exit Price *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Exit price"
                value={form.exit_price}
                onChange={(e) => setForm({ ...form, exit_price: e.target.value })}
                className="bg-slate-800 border-slate-600 text-white"
                required
                data-testid="partial-close-exit-price"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Exit Lots * (max: {remainingLots})</Label>
              <Input
                type="number"
                min="1"
                max={remainingLots}
                value={form.exit_lots}
                onChange={(e) => setForm({ ...form, exit_lots: Math.min(parseInt(e.target.value) || 1, remainingLots) })}
                className="bg-slate-800 border-slate-600 text-white"
                required
                data-testid="partial-close-exit-lots"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Exit Type</Label>
              <Select value={form.exit_type} onValueChange={(v) => setForm({ ...form, exit_type: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="partial-close-exit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {EXIT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Notes (optional)</Label>
            <Textarea
              placeholder="Add any notes about this exit..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-800 border-slate-600 text-white resize-none h-16"
              data-testid="partial-close-notes"
            />
          </div>

          {/* P&L Preview */}
          {form.exit_price && (
            <div className={`rounded-lg p-3 ${pnl.amount >= 0 ? 'bg-profit/10 border border-profit/30' : 'bg-loss/10 border border-loss/30'}`}>
              <p className="text-xs text-slate-400 mb-1">Estimated P&L for this exit:</p>
              <div className="flex justify-between items-center">
                <span className="text-slate-300">
                  {form.exit_lots} lots × {getPnlMultiplier(trade)} × {pnl.points.toFixed(2)} pts
                </span>
                <span className={`text-xl font-bold ${pnl.amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                  {pnl.amount >= 0 ? '+' : ''}₹{pnl.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          {/* Warning if closing all remaining */}
          {form.exit_lots >= remainingLots && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-400 text-sm">
              This will fully close the trade (no remaining lots).
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !form.exit_price || !form.exit_lots}
              className={pnl.amount >= 0 ? "bg-profit hover:bg-profit/80" : "bg-loss hover:bg-loss/80"}
              data-testid="partial-close-submit"
            >
              {loading ? "Processing..." : `Close ${form.exit_lots} Lot${form.exit_lots > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartialCloseModal;
